import {
  ButtonColor,
  ButtonSize,
  Collapsable,
  CollapsableGroup,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  CardApplication,
  DocumentStorage,
  Summary,
  SummaryConfirmationPageValidation,
} from 'src/front/api/apiInstance';
import { CardSelectionType, SummaryInfoResponse } from 'src/front/api/generated';
import displayPDF, { scrollTo } from 'src/front/common/utils/domUtils';

import AsyncButton from 'src/front/components/AsyncButton';

import {
  Container,
  FormElement,
  LongFormRow,
  Separator,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import InformationTable from 'src/front/components/InformationTable';
import { BREAK_POINTS } from 'src/front/constant';
import { useCardTypeState } from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';

const LineFormElement = styled(FormElement)`
  width: 850px;

  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_1X}) {
    width: 600px;
  }
`;

const LongerButton = styled(AsyncButton)`
  width: 230px;
`;

interface Props {
  customerId?: string;
  goToNextStep: (customerId?: string) => void;
}

const CardSummary: FC<Props> = ({ customerId, goToNextStep }) => {
  const { t } = useTranslation();
  const isCreditCard = useCardTypeState() === CardSelectionType.CREDITCARD;

  const [summary, setSummary] = useState<SummaryInfoResponse>();
  const [printWarning, setPrintWarning] = useState<string>();

  const loadSummary = useCallback(async () => {
    const { data } = await Summary.getSummaryInfo();

    if (data.result === 'OK') {
      setSummary(data);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const printApplication = async (): Promise<void> => {
    const { data } = await CardApplication.generateCardApplicationDocument();

    if (data.result === 'OK') {
      const { data: response } = await DocumentStorage.getDocument({
        documentId: data.documentId,
      });
      if (response.result === 'OK') {
        displayPDF(response.documentData);
      }
      printWarning && setPrintWarning(undefined);
    }
  };

  const onSubmit = async (): Promise<void> => {
    const { data } = await SummaryConfirmationPageValidation.validateSummaryPageData();

    if (data.result === 'OK') {
      if (data.isCardApplicationPrinted) {
        goToNextStep(customerId);
      } else {
        setPrintWarning('cardEntry_summary_printApplication_errorMessage');
        scrollTo(0, 0);
      }
    }
  };

  const summaryData = useMemo(
    () =>
      summary
        ? [
            {
              title: 'cardEntry_summary_productInfo_text',
              blocks: [
                {
                  title: '',
                  information: [
                    {
                      label: 'cardEntry_summary_cardPrintType_text',
                      info: summary.summaryProductInfoPage.printType,
                    },
                    ...(summary.summaryProductInfoPage.productOfferFlag
                      ? [
                          {
                            label: 'cardEntry_productInfo_productOffer_productOffer_label',
                            info: t(summary.summaryProductInfoPage.productOfferFlag),
                          },
                        ]
                      : []),
                    ...(summary.summaryProductInfoPage.transferAnotherCardflag
                      ? [
                          {
                            label:
                              'cardEntry_productInfo_productTransfer_transferToAnotherCard_text',
                            info: t(summary.summaryProductInfoPage.transferAnotherCardflag),
                          },
                          {
                            label: 'cardEntry_productInfo_productTransfer_sourceCard_text',
                            info: summary.summaryProductInfoPage.validCardInfo,
                          },
                        ]
                      : [
                          {
                            label: 'cardEntry_summary_cardUsageType_text',
                            info: summary.summaryProductInfoPage.cardUsageType,
                          },
                          {
                            label: 'cardEntry_summary_mainCardNumber_text',
                            info: summary.summaryProductInfoPage.mainCardNumber,
                          },
                        ]),
                    {
                      label: 'cardEntry_summary_customerType_text',
                      info: summary.summaryProductInfoPage.customerType,
                    },
                    {
                      label: 'cardEntry_summary_customerSubType_text',
                      info: summary.summaryProductInfoPage.customerSubType,
                    },
                    {
                      label: 'cardEntry_summary_debit_text',
                      info: summary.summaryProductInfoPage.debitFlag,
                    },
                    {
                      label: 'cardEntry_summary_resident_text',
                      info: summary.summaryProductInfoPage.resident,
                    },
                    {
                      label: 'cardEntry_summary_accountType_text',
                      info: summary.summaryProductInfoPage.accountType,
                    },
                    {
                      label: 'cardEntry_summary_brandOwner_text',
                      info: summary.summaryProductInfoPage.brandOwner,
                    },
                    {
                      label: 'cardEntry_summary_cardType_text',
                      info: summary.summaryProductInfoPage.cardType,
                    },
                  ],
                },
              ],
            },
            {
              title: 'cardEntry_summary_customerInfo_text',
              blocks: [
                {
                  title: 'cardEntry_summary_individualInfo_text',
                  information: [
                    {
                      label: 'cardEntry_summary_surname_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .customerSurname,
                    },
                    {
                      label: 'cardEntry_summary_name_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .customerName,
                    },
                    {
                      label: 'cardEntry_summary_fatherName_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .customerSecondName,
                    },
                    {
                      label: 'cardEntry_summary_birthdate_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData.birthDate,
                    },
                    {
                      label: 'cardEntry_summary_gender_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .customerGender,
                    },
                    {
                      label: 'cardEntry_summary_citizenship_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .citizenship,
                    },
                    {
                      label: 'cardEntry_summary_residency_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .residenceCountry,
                    },
                    {
                      label: 'cardEntry_summary_birthPlace_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .birthPlaceCity,
                    },
                    {
                      label: 'cardEntry_summary_previousSurname_text',
                      info:
                        summary.summaryCustomerInfoPage.summaryCustomerIndividualInfoData
                          .previousSurname,
                    },
                  ],
                },
                // eslint-disable-next-line max-len
                ...summary.summaryCustomerInfoPage.summaryCustomerDocumentInfo.documentInfoDataList.map(
                  (document, idx, documentArray) => ({
                    title: idx === 0 ? 'cardEntry_summary_documents_text' : '',
                    information: [
                      {
                        label: 'cardEntry_summary_documentType_text',
                        info: document.documentType,
                      },
                      {
                        label: 'cardEntry_summary_series_text',
                        info: document.identityDocumentSerialCode,
                      },
                      {
                        label: 'cardEntry_summary_documentNumber_text',
                        info: document.identityDocumentSerialNumber,
                      },
                      {
                        label: 'cardEntry_summary_issuedBy_text',
                        info: document.issuedBy,
                      },
                      {
                        label: 'cardEntry_summary_issueDate_text',
                        info: document.issueDate,
                      },
                      {
                        label: 'cardEntry_summary_expiredDate_text',
                        info: document.expireDate,
                      },
                      {
                        label: 'cardEntry_summary_country_text',
                        info: document.issueCountry,
                      },
                      {
                        label: 'cardEntry_summary_unitCode_text',
                        info: document.unitCode,
                      },
                    ],
                    hasEndingLine: idx !== documentArray.length - 1,
                  })
                ),
              ],
            },
            ...(isCreditCard
              ? [
                  {
                    title: 'cardEntry_summary_jobScoring_text',
                    blocks: [
                      {
                        title: 'cardEntry_summary_jobInfo_text',
                        information: [
                          {
                            label: 'cardEntry_summary_companyName_text',
                            info: summary.summaryJobScoringInfoPage.summaryJobInfoData.companyName,
                          },
                          {
                            label: 'cardEntry_summary_position_text',
                            info: summary.summaryJobScoringInfoPage.summaryJobInfoData.jobTitle,
                          },
                          {
                            label: 'cardEntry_summary_employmentType_text',
                            info: summary.summaryJobScoringInfoPage.summaryJobInfoData.occupation,
                          },
                          {
                            label: 'cardEntry_summary_affiliationOfOrganization_text',
                            info: summary.summaryJobScoringInfoPage.summaryJobInfoData.jobSector,
                          },
                          {
                            label: 'cardEntry_summary_lineOfBusiness_text',
                            info: summary.summaryJobScoringInfoPage.summaryJobInfoData.businessLine,
                          },
                          {
                            label: 'cardEntry_summary_jobsInLast3Years_text',
                            info:
                              summary.summaryJobScoringInfoPage.summaryJobInfoData
                                .jobNumberForLast3Years,
                          },
                          {
                            label: 'cardEntry_summary_numberOfEmployees_text',
                            info:
                              summary.summaryJobScoringInfoPage.summaryJobInfoData.employeeNumber,
                          },
                          {
                            label: 'cardEntry_summary_certificate2NDFL_text',
                            info:
                              summary.summaryJobScoringInfoPage.summaryJobInfoData
                                .is2NdflCertificateProvided,
                          },
                        ],
                      },
                      {
                        title: 'cardEntry_summary_limitInfo_text',
                        information: [
                          {
                            label: 'cardEntry_summary_requestedCreditLimit_text',
                            info:
                              summary.summaryJobScoringInfoPage.summaryScoringInfoData
                                .requestedLimit,
                          },
                          {
                            label: 'cardEntry_summary_monthlyIncome_text',
                            info: summary.summaryJobScoringInfoPage.summaryScoringInfoData.income,
                          },
                          {
                            label: 'cardEntry_summary_otherSourcesMonthlyIncome_text',
                            info:
                              summary.summaryJobScoringInfoPage.summaryScoringInfoData.otherIncome,
                          },
                        ],
                      },
                    ],
                  },
                ]
              : []),
            {
              title: 'cardEntry_summary_customerDetail_text',
              blocks: [
                {
                  title: 'cardEntry_summary_taxInfo_text',
                  information: [
                    {
                      label: 'cardEntry_summary_inn_text',
                      info:
                        summary.summaryCustomerDetailInfoPage.summaryCustomerTaxInfoData.taxNumber,
                    },
                  ],
                },
                // eslint-disable-next-line max-len
                ...summary.summaryCustomerDetailInfoPage.summaryCustomerTaxInfoData.taxResidencyList.map(
                  (tax) => ({
                    information: [
                      {
                        label: 'cardEntry_summary_taxResidenceCountry_text',
                        info: tax.taxResidenceCountry,
                      },
                      {
                        label: 'cardEntry_summary_iinnProvided_text',
                        info: tax.hasTaxNumberProvided,
                      },
                      {
                        label: 'cardEntry_summary_iinn_text',
                        info: tax.foreignTaxNumber,
                      },
                    ],
                  })
                ),
                {
                  title: 'cardEntry_summary_detailInformation_text',
                  information: [
                    {
                      label: 'cardEntry_summary_maritalStatus_text',
                      info:
                        summary.summaryCustomerDetailInfoPage.summaryCustomerDetailInfoData
                          .maritalStatus,
                    },
                    {
                      label: 'cardEntry_summary_education_text',
                      info:
                        summary.summaryCustomerDetailInfoPage.summaryCustomerDetailInfoData
                          .educationStatus,
                    },
                    {
                      label: 'cardEntry_summary_numberOfChildren_text',
                      info: Number.isInteger(
                        summary.summaryCustomerDetailInfoPage.summaryCustomerDetailInfoData
                          .numberOfChildren
                      )
                        ? `${summary.summaryCustomerDetailInfoPage.summaryCustomerDetailInfoData.numberOfChildren}`
                        : '',
                    },
                    {
                      label: 'cardEntry_summary_stateeEmployee_text',
                      info: t(
                        summary.summaryCustomerDetailInfoPage.summaryCustomerDetailInfoData
                          .isGovernmentOfficer || ''
                      ),
                    },
                  ],
                },
              ],
            },
            {
              title: 'cardEntry_summary_benefitsOf3rdParty_text',
              blocks: [
                {
                  information: [
                    {
                      label: 'cardEntry_summary_actBenefitOf3rdParty_text',
                      info: summary.summaryBenefitsThirdPartyInfoPage.hasBenefitsThirdParty,
                    },
                  ],
                },
                ...(summary.summaryBenefitsThirdPartyInfoPage.summaryBenefitsThirdPartyInfoData
                  ? [
                      {
                        title: 'cardEntry_summary_beneficaryInformation_subtitle',
                        information: [
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_beneficaryName_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.beneficiaryName,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_placeofBirth_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.birthPlace,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_dateOfBirth_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.birthDate,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_documentType_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.identityType,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_series_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.identityDocumentSerialCode,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_documentNumber_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.identityDocumentSerialNumber,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_issuedBy_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.issuedBy,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_issuedDate_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.issueDate,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_unitCode_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.unitCode,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_inn_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage
                                .summaryBenefitsThirdPartyInfoData.inn,
                          },
                        ],
                      },
                    ]
                  : []),
                {
                  information: [
                    {
                      label: 'cardEntry_summary_controlYourActions_text',
                      info: summary.summaryBenefitsThirdPartyInfoPage.hasControlPerson,
                    },
                  ],
                },
                ...(summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                  ? [
                      {
                        title: 'cardEntry_summary_beneficaryInformation_subtitle',
                        information: [
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_beneficaryName_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .beneficiaryName,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_placeofBirth_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .birthPlace,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_dateOfBirth_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .birthDate,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_documentType_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .identityType,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_series_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .identityDocumentSerialCode,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_documentNumber_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .identityDocumentSerialNumber,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_issuedBy_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .issuedBy,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_issuedDate_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .issueDate,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_unitCode_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .unitCode,
                          },
                          {
                            label: 'cardEntry_summary_3rdPartyBenefit_inn_text',
                            info:
                              summary.summaryBenefitsThirdPartyInfoPage.summaryControlPersonInfoData
                                .inn,
                          },
                        ],
                      },
                    ]
                  : []),
              ],
            },
            {
              title: 'cardEntry_summary_contactInfo_text',
              blocks: [
                ...summary.summaryContactInfoPage.summaryAddressInfoData.addressList.map(
                  (address, idx, addressArray) => ({
                    title: idx === 0 ? 'cardEntry_summary_addresses_text' : '',
                    information: [
                      {
                        label: address.addressType,
                        info: address.addressInfo,
                      },
                      {
                        label: 'cardEntry_summary_registrationDate_text',
                        info: address.registrationDate,
                      },
                      {
                        label: 'cardEntry_summary_zipCode_text',
                        info: address.zipCode,
                      },
                      {
                        label: 'cardEntry_summary_correspondenceAddress_text',
                        // TODO(ozgunbal): change property name to mw, it's not a flag actually
                        info: address.hasCorrespondenceFlag
                          ? t('cardEntry_summary_correspondence_text', {
                              addressType: address.hasCorrespondenceFlag,
                            })
                          : '',
                      },
                      {
                        label: 'cardEntry_summary_propertyStatusOfActualResidence_text',
                        info: address.addressHomeOwnerType,
                      },
                    ],
                    hasEndingLine: idx !== addressArray.length - 1,
                  })
                ),
                {
                  title: 'cardEntry_summary_phone_text',
                  information: [
                    {
                      label: 'cardEntry_summary_mobilePhone_text',
                      info:
                        summary.summaryContactInfoPage.summaryContactDetailInfoData
                          .mobilePhoneNumber,
                    },
                    {
                      label: 'cardEntry_summary_actualLivingAddressPhone_text',
                      info:
                        summary.summaryContactInfoPage.summaryContactDetailInfoData.homePhoneNumber,
                    },
                    {
                      label: 'cardEntry_summary_workPhone_text',
                      info:
                        summary.summaryContactInfoPage.summaryContactDetailInfoData.workPhoneNumber,
                    },
                    {
                      label: 'cardEntry_summary_contactPersonPhone1_text',
                      info:
                        summary.summaryContactInfoPage.summaryContactDetailInfoData
                          .contactPhone1Number,
                    },
                    {
                      label: 'cardEntry_summary_contactPersonPhone2_text',
                      info:
                        summary.summaryContactInfoPage.summaryContactDetailInfoData
                          .contactPhone2Number,
                    },
                  ],
                },
                {
                  title: 'cardEntry_summary_email_text',
                  information: [
                    {
                      label: 'cardEntry_summary_emailAddress_text',
                      info:
                        summary.summaryContactInfoPage.summaryContactDetailInfoData.emailAddress,
                    },
                  ],
                },
              ],
            },
            {
              title: 'cardEntry_summary_cardInfo_text',
              blocks: [
                {
                  title: 'cardEntry_summary_applicationInfo_text',
                  information: [
                    {
                      label: 'cardEntry_summary_embossedName_text',
                      info: summary.summaryCardInfoPage.summaryApplicationInfoData.cardEmbossedName,
                    },
                    {
                      label: 'cardEntry_summary_branch_text',
                      info: summary.summaryCardInfoPage.summaryApplicationInfoData.branchName,
                    },
                    {
                      label: 'cardEntry_summary_applicationDate_text',
                      info: summary.summaryCardInfoPage.summaryApplicationInfoData.applicationDate,
                    },
                  ],
                },
                {
                  title: 'cardEntry_summary_sendingInfo_text',
                  information: [
                    {
                      label: 'cardEntry_summary_cardSendingAddress_text',
                      info:
                        summary.summaryCardInfoPage.summarySendingInfoData.cardDeliveryAddress ||
                        '',
                    },
                    {
                      label: 'cardEntry_summary_branch_text',
                      info:
                        summary.summaryCardInfoPage.summarySendingInfoData.cardDeliveryBranch || '',
                    },
                    {
                      label: 'cardEntry_summary_shippingAddressPinCode_text',
                      info:
                        summary.summaryCardInfoPage.summarySendingInfoData.pinCodeDeliveryAddress ||
                        '',
                    },
                    {
                      label: 'cardEntry_summary_shippingBranch_text',
                      info:
                        summary.summaryCardInfoPage.summarySendingInfoData.pinCodeDeliveryBranch ||
                        '',
                    },
                    {
                      label: 'cardEntry_summary_smsTransactionAlert_text',
                      info: t(summary.summaryCardInfoPage.summarySendingInfoData.smsAlert || ''),
                    },
                    {
                      label: 'cardEntry_summary_periodOfReceiptStatement_text',
                      info:
                        summary.summaryCardInfoPage.summarySendingInfoData.statementPeriod || '',
                    },
                    {
                      label: 'cardEntry_summary_sendStatementEmail_text',
                      info: t(
                        summary.summaryCardInfoPage.summarySendingInfoData.sendViaEmail || ''
                      ),
                    },
                    {
                      label: 'cardEntry_summary_lifeInsurance_text',
                      info: t(
                        summary.summaryCardInfoPage.summarySendingInfoData.lifeInsurance || ''
                      ),
                    },
                  ],
                },
              ],
            },
          ]
        : [],
    [isCreditCard, summary, t]
  );

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_summary_title" />
      </Title>
      <LongFormRow>
        <Trans i18nKey="cardEntry_summary_text" />
      </LongFormRow>
      {summary?.isPrintButtonVisible ? (
        <LongFormRow>
          <LongerButton
            color={ButtonColor.Secondary}
            onClick={printApplication}
            size={ButtonSize.Medium}
            text={t('cardEntry_summary_printApplication_button')}
            type="button"
          />
        </LongFormRow>
      ) : null}

      {printWarning ? (
        <LongFormRow>
          <WarningText>
            <Trans i18nKey={printWarning} />
          </WarningText>
        </LongFormRow>
      ) : null}

      <LongFormRow>
        <CollapsableGroup>
          {summaryData.map((sum) => (
            <Collapsable key={sum.title} title={t(sum.title)}>
              <InformationTable items={sum.blocks} />
            </Collapsable>
          ))}
        </CollapsableGroup>
      </LongFormRow>

      <Separator />
      <LineFormElement>
        <Trans i18nKey="cardEntry_summary_remoteBankingServiceRules_text" />{' '}
        <a
          href="https:/www.crediteurope.ru/upload/files/privately/docs/dbo.pdf"
          rel="noopener noreferrer"
          target="_blank"
        >
          <Trans i18nKey="cardEntry_summary_rbsRuleLink_text" />
        </a>
      </LineFormElement>
      <LongFormRow>
        <AsyncButton
          color={ButtonColor.Primary}
          onClick={onSubmit}
          size={ButtonSize.Medium}
          text={t('cardEntry_summary_approve_button')}
          type="button"
        />
      </LongFormRow>
    </Container>
  );
};

export default CardSummary;
